import { useState, useCallback, useContext } from 'react';
import { RefreshCcw } from 'lucide-react';
import { OverlayContext } from '@/hooks/overlay-context';
import { useI18n } from '@/hooks/useI18n';
import { useNavigate, useLocation } from 'react-router-dom';

type Props = {
  soundURL?: string;
  initialCanStart?: boolean; // Optional: set initial start capability
  fitParent?: boolean; // Optional: whether to fit parent container or be full screen (default: false)
  startText?: string; // Optional: text to display when can start (default: 'BEGIN')
  waitingText?: string; // Optional: text to display when can't start (default: 'WAITING')
  useOverlayRefresh?: boolean; // Optional: whether to use overlay context refresh instead of window reload (default: true)
  onStart?: () => void;
  onReset?: () => void;
};

const languages = [
  { code: 'ja', label: '日本語 >' },
  { code: 'en', label: 'English >' },
  { code: 'zh', label: '中文 >' },
];

export const StartOverlay = ({
  soundURL,
  initialCanStart = true, // Default to able to start
  fitParent = false, // Default to full screen overlay
  startText, // Optional - will use translation if not provided
  waitingText, // Optional - will use translation if not provided
  useOverlayRefresh = true, // Default to using overlay refresh
  onStart: onStartProp,
  onReset: onResetProp,
}: Props) => {
  const { t, changeLanguage } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [isStarted, setIsStarted] = useState(false);
  const [canStart, setCanStart] = useState(initialCanStart);

  // Try to get the overlay context, but don't require it to exist
  const overlayContext = useContext(OverlayContext);

  const handleLanguageStart = useCallback((langCode: string) => {
    // 1. Change Language via URL (Router-based app usually relies on URL)
    const pathParts = location.pathname.split('/');
    // Ensure path starts with slash so split gives ['', 'lang', ...]
    if (pathParts.length >= 2) {
       pathParts[1] = langCode;
       const newPath = pathParts.join('/');
       navigate(newPath);
    }
    
    // Also call changeLanguage for immediate effect in i18next
    changeLanguage(langCode);

    // 2. Play Sound
    if(soundURL) {
      try {
        new Audio(soundURL).play();
      } catch (error) {
        console.error('Audio playback failed:', error);
      }
    }

    // 3. Start Animation / Transition
    setTimeout(() => {
      setIsStarted(true);
      if (onStartProp) {
        onStartProp();
      }
      
      // 4. Trigger visual cue for language selector in main UI
      // Dispatch event slightly later to ensure UI is ready
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('highlight-language-selector'));
      }, 100);
      
    }, 300);
  }, [onStartProp, soundURL, changeLanguage, location, navigate]);

  const handleReset = useCallback(() => {
    setIsStarted(false);
    if (onResetProp) {
      onResetProp();
    } else if (useOverlayRefresh && overlayContext) {
      // Use the overlay context's refresh function if available
      overlayContext.refresh();
    } else {
      // Fallback to window reload if no context or useOverlayRefresh is false
      window.location.reload();
    }
  }, [onResetProp, useOverlayRefresh, overlayContext]);

  return isStarted ? (
    null
  ) : (
    <div className={`${fitParent ? 'absolute inset-0' : 'fixed inset-0'} z-50 flex flex-col items-center justify-between py-16 px-8 bg-[color:var(--chat-overlay-bg)] backdrop-blur-md transition-colors duration-500`}>
      
      {/* Top Section: Intro / Graphic Placeholder */}
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-md space-y-8">
        {/* Graphic Placeholder */}
        <div className="w-full aspect-video bg-gradient-to-br from-white/40 to-white/10 rounded-2xl border border-white/30 shadow-lg flex items-center justify-center overflow-hidden backdrop-blur-sm">
          <div className="text-center p-6">
            <p className="text-gray-500 font-medium italic opacity-70">
              {/* Placeholder for Intro Graphic */}
              Welcome to our AI Assistant
            </p>
          </div>
        </div>

        {/* Intro Text */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl md:text-3xl font-bold text-[color:var(--msg-asst-text)]">
            {t('startOverlay.welcomeTitle', 'Hello & Welcome')}
          </h2>
          <p className="text-[color:var(--msg-asst-text)]/80 max-w-xs mx-auto leading-relaxed">
            {t('startOverlay.welcomeDescription', 'Select your language to begin the conversation.')}
          </p>
        </div>
      </div>

      {/* Bottom Section: Language Selection Buttons */}
      <div className="w-full max-w-md flex gap-4 justify-center items-center mt-8">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageStart(lang.code)}
            disabled={!canStart}
            className="flex-1 py-4 rounded-2xl border-2 border-[color:var(--btn-primary-from)] bg-[color:var(--btn-primary-to)] text-white shadow-[0_4px_14px_0_var(--btn-primary-shadow)] cursor-pointer text-lg font-bold transition-all duration-200 hover:scale-105 hover:-translate-y-1 active:scale-95 disabled:cursor-not-allowed disabled:bg-gray-500 disabled:border-gray-400 disabled:shadow-none"
          >
            {lang.label}
          </button>
        ))}
      </div>
    </div>
  );
};